import { useState, useEffect } from 'react';

const API = '/api';
const BG_URL = '/vendor_login_bg.png'; // Handled by the public folder/proxy later, but for now we refer to the artifact name if possible or just use a placeholder if needed. 
// Wait, I should probably refer to the actual path if I can or the browser subagent will see it.
// Actually, I'll just use a high-quality placeholder or the generated image if I can guess the URL.
// In this environment, artifacts are often served or mapped. 

/* ── Minimal inline SVG icon ── */
const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    mail: ['M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z', 'M22 6l-10 7L2 6'],
    lock: ['M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z', 'M7 11V7a5 5 0 0 1 10 0v4'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    eyeOff: ['M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94', 'M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19', 'M1 1l22 22'],
    building: ['M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9 22V12h6v10'],
    arrow: 'M5 12h14M12 5l7 7-7 7',
    info: 'M12 16v-4M12 8h.01 M12 2a10 10 0 100 20 10 10 0 000-20z',
};

export default function VendorLogin({ onLogin }) {
    const [form, setForm] = useState({ email: '', password: '' });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [branding, setBranding] = useState(null);
    const [brandingLoading, setBrandingLoading] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');
        const opId = params.get('op');
        if (opId) {
            fetch(`${API}/operators/${opId}`)
                .then(res => res.json())
                .then(data => {
                    if (data && data.companyName) setBranding(data);
                })
                .catch(err => console.error("Error fetching branding:", err));
        }
    }, []);

    const handleEmailBlur = async () => {
        if (!form.email || !form.email.includes('@') || branding) return;
        setBrandingLoading(true);
        try {
            const res = await fetch(`${API}/vendor-branding?email=${encodeURIComponent(form.email)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) setBranding(data);
            }
        } catch (err) {
            // silently fail — branding is cosmetic
        } finally {
            setBrandingLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API}/vendor-login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: form.email, password: form.password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Login failed');
            localStorage.setItem('ops-user', JSON.stringify(data));
            onLogin(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            {/* Left Side: Image & Branding */}
            <div style={styles.visualSide}>
                <div style={styles.overlay} />
                <img
                    src="/vendor_login_bg.png"
                    alt="Logistics Background"
                    style={styles.bgImage}
                />
                <div style={styles.visualContent}>
                    <div style={styles.brandGroup}>
                        <div style={styles.logoBox}>
                            {branding?.companyLogo ? (
                                <img
                                    src={`${API}/uploads/${branding.companyLogo}`}
                                    alt={branding.companyName}
                                    style={{ width: '100%', height: '100%', borderRadius: 16, objectFit: 'contain' }}
                                />
                            ) : (
                                <Icon d={icons.building} size={32} color="white" />
                            )}
                        </div>
                        <div style={styles.brandTexts}>
                            {branding?.companyName ? (
                                <h2 style={styles.brandNameTitle}>{branding.companyName}</h2>
                            ) : brandingLoading ? (
                                <h2 style={{ ...styles.brandNameTitle, color: 'rgba(255,255,255,0.4)', fontSize: 18 }}>…</h2>
                            ) : null}
                            <p style={styles.brandTagline}>Vendor Fleet Management</p>
                        </div>
                    </div>

                    <div style={styles.visualQuotes}>
                        <h3 style={styles.quoteTitle}>Manage Fleet, Drivers & Operations</h3>
                        <p style={styles.quoteText}>Assign drivers, manage your fleet vehicles, and track all operations assigned to you — in one place.</p>
                    </div>

                    <div style={styles.visualFooter}>
                        <div style={styles.statItem}>
                            <span style={styles.statVal}>Fleet</span>
                            <span style={styles.statLabel}>Vehicles</span>
                        </div>
                        <div style={styles.statItem}>
                            <span style={styles.statVal}>Drivers</span>
                            <span style={styles.statLabel}>Management</span>
                        </div>
                        <div style={styles.statItem}>
                            <span style={styles.statVal}>Trips</span>
                            <span style={styles.statLabel}>Operations</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Login Form */}
            <div style={styles.formSide}>
                <div style={styles.formContainer}>
                    <div style={styles.mobileBrand}>
                        {branding?.companyLogo ? (
                            <img
                                src={`${API}/uploads/${branding.companyLogo}`}
                                alt={branding.companyName}
                                style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'contain' }}
                            />
                        ) : (
                            <Icon d={icons.building} size={24} color="#10b981" />
                        )}
                        {branding?.companyName && <span style={styles.mobileBrandName}>{branding.companyName}</span>}
                    </div>

                    <div style={styles.header}>
                        <h1 style={styles.title}>Vendor Partner Login</h1>
                        <p style={styles.subtitle}>Welcome back! Please enter your details.</p>
                    </div>

                    <form onSubmit={handleSubmit} style={styles.form}>
                        {error && (
                            <div style={styles.errorBox}>
                                <Icon d={icons.info} size={18} color="#ef4444" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Email Address</label>
                            <div style={styles.inputWrapper}>
                                <span style={styles.inputIcon}>
                                    <Icon d={icons.mail} size={18} color="#94a3b8" />
                                </span>
                                <input
                                    style={styles.input}
                                    type="email"
                                    placeholder="your@email.com"
                                    required
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                    onBlur={handleEmailBlur}
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div style={styles.inputGroup}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={styles.label}>Password</label>
                                <a href="#" style={styles.forgotLink}>Forgot password?</a>
                            </div>
                            <div style={styles.inputWrapper}>
                                <span style={styles.inputIcon}>
                                    <Icon d={icons.lock} size={18} color="#94a3b8" />
                                </span>
                                <input
                                    style={{ ...styles.input, paddingRight: 48 }}
                                    type={showPass ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    required
                                    value={form.password}
                                    onChange={e => setForm({ ...form, password: e.target.value })}
                                />
                                <button type="button" onClick={() => setShowPass(!showPass)} style={styles.eyeBtn}>
                                    <Icon d={showPass ? icons.eyeOff : icons.eye} size={18} color="#94a3b8" />
                                </button>
                            </div>
                        </div>

                        <div style={styles.rememberMe}>
                            <input type="checkbox" id="remember" style={styles.checkbox} />
                            <label htmlFor="remember" style={styles.checkboxLabel}>Remember for 30 days</label>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
                        >
                            {loading ? 'Authenticating...' : 'Sign In'}
                        </button>
                    </form>

                    <div style={styles.divider}>
                        <span style={styles.dividerLine} />
                        <span style={styles.dividerText}>OR</span>
                        <span style={styles.dividerLine} />
                    </div>

                    <a href="#operator-login"
                        style={styles.operatorBtn}
                        onClick={e => { e.preventDefault(); window.location.hash = 'operator-login'; window.location.reload(); }}>
                        Login as Operator Dashboard
                    </a>

                    <p style={styles.footer}>
                        Don't have an account? <span style={styles.footerAction}>Contact Administrator</span>
                    </p>
                </div>
            </div>
        </div>
    );
}

const styles = {
    page: {
        height: '100vh', width: '100%', display: 'flex', overflow: 'hidden',
        background: '#fff', fontFamily: "'Inter', sans-serif"
    },
    visualSide: {
        flex: '1.2', position: 'relative', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', padding: '0 8%', background: '#064e3b',
        // Hide on small screens
        '@media (max-width: 900px)': { display: 'none' }
    },
    bgImage: {
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', opacity: 0.4
    },
    overlay: {
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, rgba(6,78,59,0.9) 0%, rgba(6,78,59,0.6) 100%)',
        zIndex: 1
    },
    visualContent: { position: 'relative', zIndex: 2, color: '#fff' },
    brandGroup: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 80 },
    logoBox: {
        width: 60, height: 60, borderRadius: 16, background: '#10b981',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(16,185,129,0.3)'
    },
    brandTexts: { display: 'flex', flexDirection: 'column' },
    brandNameTitle: { fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' },
    brandTagline: { fontSize: 13, color: '#6ee7b7', margin: 0, fontWeight: 500 },

    visualQuotes: { marginBottom: 60, maxWidth: 440 },
    quoteTitle: { fontSize: 36, fontWeight: 800, marginBottom: 16, lineHeight: 1.1 },
    quoteText: { fontSize: 18, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 },

    visualFooter: { display: 'flex', gap: 40 },
    statItem: { display: 'flex', flexDirection: 'column', gap: 4 },
    statVal: { fontSize: 24, fontWeight: 700, color: '#10b981' },
    statLabel: { fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' },

    formSide: {
        flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#fff', padding: 40, borderLeft: '1px solid #f1f5f9'
    },
    formContainer: { width: '100%', maxWidth: 420 },
    mobileBrand: {
        display: 'none', alignItems: 'center', gap: 10, marginBottom: 40,
        '@media (max-width: 900px)': { display: 'flex' }
    },
    mobileBrandName: { fontSize: 20, fontWeight: 800, color: '#064e3b' },

    header: { marginBottom: 40 },
    title: { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' },
    subtitle: { fontSize: 16, color: '#64748b', margin: 0 },

    form: { display: 'flex', flexDirection: 'column', gap: 24 },
    errorBox: {
        background: '#fff1f2', border: '1px solid #ffe4e6', borderRadius: 12,
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
        color: '#be123c', fontSize: 14, fontWeight: 600
    },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: 8 },
    label: { fontSize: 14, fontWeight: 600, color: '#334155' },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: { position: 'absolute', left: 16, display: 'flex', alignItems: 'center' },
    input: {
        width: '100%', padding: '12px 16px 12px 48px', borderRadius: 12,
        border: '1px solid #e2e8f0', fontSize: 15, background: '#f8fafc',
        outline: 'none', transition: 'all 0.2s', color: '#1e293b'
    },
    forgotLink: { fontSize: 13, fontWeight: 600, color: '#10b981', textDecoration: 'none' },
    eyeBtn: { position: 'absolute', right: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 4 },

    rememberMe: { display: 'flex', alignItems: 'center', gap: 10 },
    checkbox: { width: 16, height: 16, borderRadius: 4, cursor: 'pointer' },
    checkboxLabel: { fontSize: 14, color: '#64748b', fontWeight: 500, cursor: 'pointer' },

    submitBtn: {
        background: '#10b981', color: '#fff', border: 'none', borderRadius: 12,
        padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
        transition: 'background 0.2s, transform 0.1s', boxShadow: '0 4px 12px rgba(16,185,129,0.2)'
    },

    divider: { display: 'flex', alignItems: 'center', gap: 16, margin: '32px 0' },
    dividerLine: { flex: 1, height: 1, background: '#f1f5f9' },
    dividerText: { fontSize: 12, fontWeight: 700, color: '#94a3b8' },

    operatorBtn: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        padding: '12px', border: '1px solid #e2e8f0', borderRadius: 12,
        textDecoration: 'none', color: '#334155', fontSize: 14, fontWeight: 600,
        background: '#fff', transition: 'all 0.2s'
    },

    footer: { marginTop: 32, textAlign: 'center', fontSize: 14, color: '#64748b' },
    footerAction: { color: '#10b981', fontWeight: 700, cursor: 'pointer' }
};
